import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import {
  HsmMessageLog,
  HsmCampaignStats,
  HsmDeliveryStatus
} from '../../types';

// Helper to get badge variant for delivery status
function getDeliveryStatusBadge(status: HsmDeliveryStatus | string | undefined | null) {
  if (!status) return <Badge variant="secondary">-</Badge>;

  const statusMap: Record<string, { variant: 'sent' | 'delivered' | 'read' | 'failed' | 'secondary'; label: string }> = {
    sent: { variant: 'sent', label: 'Enviado' },
    delivered: { variant: 'delivered', label: 'Entregue' },
    read: { variant: 'read', label: 'Lido' },
    failed: { variant: 'failed', label: 'Falhou' }
  };

  const config = statusMap[status.toLowerCase()] || { variant: 'secondary' as const, label: status };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export interface HsmLogsTabProps {
  // State
  messageLogs: HsmMessageLog[];
  logsLoading: boolean;
  campaignStats: HsmCampaignStats | null;
  statsLoading: boolean;

  // Handlers
  fetchMessageLogs: () => Promise<{ success: boolean; error?: string }>;
  fetchCampaignStats: () => Promise<{ success: boolean; error?: string }>;
}

export function HsmLogsTab({
  messageLogs,
  logsLoading,
  campaignStats,
  statsLoading,
  fetchMessageLogs,
  fetchCampaignStats
}: HsmLogsTabProps) {
  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      {campaignStats && (
        <div className="grid gap-3 md:grid-cols-3 mb-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {campaignStats.totalSent}
            </div>
            <div className="text-sm text-muted-foreground">Mensagens Enviadas</div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="text-3xl font-bold text-red-600 dark:text-red-400">
              {campaignStats.totalFailed}
            </div>
            <div className="text-sm text-muted-foreground">Falhas</div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {campaignStats.totalSent > 0
                ? (((campaignStats.totalSent - campaignStats.totalFailed) / campaignStats.totalSent) * 100).toFixed(1)
                : 0}%
            </div>
            <div className="text-sm text-muted-foreground">Taxa de Sucesso</div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Historico de mensagens enviadas.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchCampaignStats} disabled={statsLoading}>
            {statsLoading ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Atualizar Stats
          </Button>
          <Button variant="outline" onClick={() => fetchMessageLogs()} disabled={logsLoading}>
            <RefreshCw className={cn('h-4 w-4', logsLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Telefone</TableHead>
              <TableHead>Template</TableHead>
              <TableHead className="text-center">Dia</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Entrega</TableHead>
              <TableHead>Enviado em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logsLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : messageLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm">
                  Nenhum log encontrado.
                </TableCell>
              </TableRow>
            ) : (
              messageLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-medium">
                    {log.contactPhoneNumber || '-'}
                  </TableCell>
                  <TableCell>{log.templateName}</TableCell>
                  <TableCell className="text-center">{log.campaignDay}</TableCell>
                  <TableCell>
                    <Badge variant={log.status === 'SENT' ? 'sent' : 'failed'}>
                      {log.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{getDeliveryStatusBadge(log.deliveryStatus)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(log.sentAt).toLocaleString('pt-BR')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
